import { DecimalPipe } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  ViewEncapsulation,
} from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { TranslateModule } from "@ngx-translate/core";
import { tap } from "rxjs";
import { MaterialModule } from "src/app/material.module";
import { ProviderClaimTypesService } from "src/app/services/apps/providers/provider-claim-types.service";
import { ProviderService } from "src/app/services/apps/providers/provider.service";
import {
  resolveSupportContextError,
  type SupportHistoryRecordOptions,
} from "src/app/shared/helpers/contact-support-history.helper";
import {
  recordApiRequestFailed,
  recordApiRequestStarted,
  recordApiRequestSucceeded,
} from "src/app/shared/helpers/support-api-history.helper";
import {
  eventAction,
  ViewContextHistoryStateService,
} from "src/app/shared/services/view-context-history-state.service";
import {
  buildBillingOverviewChartSlices,
  collectBillingOverviewClaimTypeValuesFromCache,
  resolveBillingOverviewNextTierLabel,
  resolveBillingOverviewProgressMarkerAnchor,
  resolveBillingOverviewUsageProgressPercent,
} from "../../billing/billing-status-widget/billing-service-overview.mapper";
import {
  BILLING_OVERVIEW_CLAIM_TYPE_ORDER,
  BillingServiceOverview,
} from "../../billing/billing-status-widget/billing-service-overview.model";
import { BillingServiceOverviewStore } from "../../billing/billing-status-widget/billing-service-overview.store";
import { UsageOverviewDialogService } from "../../billing/billing-status-widget/usage-overview-dialog.service";
import { resolveBillingOverviewChartColors } from "../../billing/billing-status-widget/billing-status-widget.chart-colors";
import { toTranslateParams } from "src/app/shared/helpers/translate-params.helper";

type DashboardUsageWidgetSupportState = {
  usageOverview?: BillingServiceOverview | null;
  usageOverviewError?: string | null;
};

const MAX_BREAKDOWN_CLAIM_TYPES = 7;

@Component({
  selector: "app-dashboard-usage-widget",
  standalone: true,
  imports: [
    MaterialModule,
    TranslateModule,
    DecimalPipe,
  ],
  templateUrl: "./dashboard-usage-widget.component.html",
  styleUrls: ["./dashboard-usage-widget.component.scss"],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardUsageWidgetComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly providerClaimTypesSvc = inject(ProviderClaimTypesService);
  private readonly providerSvc = inject(ProviderService);
  private readonly usageOverviewStore = inject(BillingServiceOverviewStore);
  private readonly usageOverviewDialog = inject(UsageOverviewDialogService);
  private readonly viewContextHistorySvc =
    inject(ViewContextHistoryStateService<DashboardUsageWidgetSupportState>);

  protected readonly tParams = toTranslateParams;

  protected readonly overview = this.usageOverviewStore.overview;
  protected readonly loadError = this.usageOverviewStore.hasLoadError;
  protected readonly refreshing = this.usageOverviewStore.refreshing;

  protected readonly vm = computed(() => {
    const overview = this.overview();
    if (!overview) {
      return null;
    }

    const providerClaimTypes = this.providerClaimTypesSvc.providerClaimTypes();
    const providers = this.providerSvc.providers();
    const orderedClaimTypeValues = collectBillingOverviewClaimTypeValuesFromCache(
      providerClaimTypes,
      providers,
    );
    const claimTypeValues = orderedClaimTypeValues.length
      ? orderedClaimTypeValues
      : [...BILLING_OVERVIEW_CLAIM_TYPE_ORDER];
    const chartColors = resolveBillingOverviewChartColors(claimTypeValues);
    const progressPercent = resolveBillingOverviewUsageProgressPercent(overview);
    const chartSlices = buildBillingOverviewChartSlices(
      overview,
      claimTypeValues,
      chartColors,
    );
    const topSlices = [...chartSlices]
      .filter((slice) => slice.count > 0)
      .sort((a, b) => b.count - a.count)
      .slice(0, MAX_BREAKDOWN_CLAIM_TYPES);
    const progressSlices = chartSlices.filter((slice) => slice.count > 0);

    return {
      claimCount: overview.usage.claimCount,
      progressPercent,
      progressMarkerAnchor: resolveBillingOverviewProgressMarkerAnchor(progressPercent),
      nextTierThreshold: resolveBillingOverviewNextTierLabel(overview.currentServicePlan),
      currentTier: overview.currentServicePlan.tier,
      chartSlices,
      progressSlices,
      topSlices,
    };
  });

  protected onRefreshClick(event: MouseEvent): void {
    event.stopPropagation();
    this.refreshUsageOverview();
  }

  protected onViewDetailsClick(): void {
    this.usageOverviewDialog.openFromCache();
  }

  private refreshUsageOverview(): void {
    recordApiRequestStarted(
      (eventAction, description) => this.recordSupportHistory(eventAction, description),
      "refresh dashboard usage overview",
    );

    this.usageOverviewStore
      .refresh()
      .pipe(
        tap((overview) => {
          if (overview) {
            recordApiRequestSucceeded(
              (eventAction, description) =>
                this.recordSupportHistory(eventAction, description),
              "refresh dashboard usage overview",
            );
            return;
          }

          const errorMessage = this.usageOverviewStore.errorMessage();
          if (errorMessage) {
            recordApiRequestFailed(
              (eventAction, description, options) =>
                this.recordSupportHistory(eventAction, description, options),
              "refresh dashboard usage overview",
              { error: errorMessage },
              "fetch",
            );
          }
        }),
      )
      .subscribe();
  }

  private recordSupportHistory(
    eventAction: eventAction,
    eventDescription: string,
    options?: SupportHistoryRecordOptions,
  ): void {
    this.viewContextHistorySvc.createContext({
      titleView: "Dashboard",
      eventAction,
      eventDescription,
      thisState: {
        usageOverview: this.usageOverviewStore.overview(),
        usageOverviewError: this.usageOverviewStore.errorMessage(),
      },
      route: this.route.snapshot,
      error: resolveSupportContextError(eventAction, options?.error),
      setAutomaticallyScreenshot: false,
    });
  }
}
